import React, { useMemo, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MaterialIcon from './MaterialIcon';
import LanguageContext from '../helper/LanguageContext';
import './LocationMapPreview.css';

/**
 * Coordinates of a location message. Reads the mobile format
 * `http://www.google.com/maps/place/{lat},{lng}/@{lat},{lng},17z`, `@lat,lng`, `?q=lat,lng`
 * and old web links `https://maps.google.com/?daddr={address}_Location` (coordinates only
 * when the address holds them).
 */
export function extractCoords(url) {
  if (!url) return null;
  let decoded = String(url);
  try {
    decoded = decodeURIComponent(decoded);
  } catch (e) {
    /* keep the raw text */
  }

  const placeMatch = decoded.match(/maps\/place\/([-\d.]+),\s*([-\d.]+)/);
  if (placeMatch) return { lat: parseFloat(placeMatch[1]), lng: parseFloat(placeMatch[2]) };

  const atMatch = decoded.match(/@([-\d.]+),([-\d.]+)/);
  if (atMatch) return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };

  const qMatch = decoded.match(/[?&]q=([-\d.]+),([-\d.]+)/);
  if (qMatch) return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };

  const daddrMatch = decoded.match(/[?&]daddr=([^&]+?)(?:_Meeting\s*Point|_Location)?$/i);
  if (daddrMatch) {
    const coordsInAddr = daddrMatch[1].match(/([-\d.]+),\s*([-\d.]+)/);
    if (coordsInAddr) return { lat: parseFloat(coordsInAddr[1]), lng: parseFloat(coordsInAddr[2]) };
  }

  return null;
}

/** Old web links name the kind ("_Meeting Point") and carry an address. */
function parseLegacy(url) {
  if (!url) return { meeting: false, address: '' };
  let decoded = String(url);
  try {
    decoded = decodeURIComponent(decoded);
  } catch (e) {
    /* keep the raw text */
  }
  const meeting = /_Meeting\s*Point$/i.test(decoded) || /Meeting\s*Point/i.test(decoded);
  const daddr = decoded.match(/[?&]daddr=([^&]+?)(?:_Meeting\s*Point|_Location)?$/i);
  return { meeting, address: daddr ? daddr[1].trim() : '' };
}

const pinSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="23" viewBox="0 0 36 46" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));">' +
  '<path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 28 18 28s18-14.5 18-28C36 8.06 27.94 0 18 0z" fill="var(--kjuup-accent)"/>' +
  '<circle cx="18" cy="17" r="7" fill="#fff"/>' +
  '</svg>';

const fmt = (n) => (Number.isFinite(n) ? n.toFixed(5) : '');

/**
 * Location message. With the label (default): the mobile Pro location card — 240 wide,
 * radius 12, 1px line, a 110 map, the title "Standort" (or "Treffpunkt" for old meeting
 * point links) and "lat, lng" to 5 decimals (the address for old links without
 * coordinates). showLabel={false}: the bare map of `height` (reply previews).
 */
export default function LocationMapPreview({ url, height, showLabel = true }) {
  const { strings, isGerm } = React.useContext(LanguageContext);
  const coords = useMemo(() => extractCoords(url), [url]);
  const legacy = useMemo(() => parseLegacy(url), [url]);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const card = showLabel;

  useEffect(() => {
    if (!coords || !containerRef.current) return undefined;
    if (mapRef.current) return undefined;

    const map = L.map(containerRef.current, {
      center: [coords.lat, coords.lng],
      zoom: card ? 15 : 17,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      keyboard: false,
      boxZoom: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
    mapRef.current = map;

    return function () {
      map.remove();
      mapRef.current = null;
    };
  }, [coords, card]);

  const title = legacy.meeting
    ? ((strings && strings.chat_share_meeting_point) || (isGerm ? 'Treffpunkt' : 'Meeting point'))
    : ((strings && strings.location) || (isGerm ? 'Standort' : 'Location'));
  const sub = coords ? `${fmt(coords.lat)}, ${fmt(coords.lng)}` : legacy.address;
  const mapHeight = height == null ? (card ? 110 : 180) : height;
  const sizeVar = typeof mapHeight === 'number' ? mapHeight + 'px' : mapHeight;

  const mapArea = coords ? (
    <div className="location-map-preview__map">
      <div ref={containerRef} className="location-map-preview-frame location-map-preview__frame" />
      <div className="location-map-preview__pin" dangerouslySetInnerHTML={{ __html: pinSvg }} />
    </div>
  ) : (
    <div className="location-map-preview-fallback location-map-preview__fallback">
      <MaterialIcon icon="location_on" size={card ? 32 : 40} color="#737373" />
    </div>
  );

  if (!card) {
    return (
      <div className="location-map-preview location-map-preview__root" style={{ '--lmp-size': sizeVar }}>
        <a href={url} target="_blank" rel="noopener noreferrer" className="location-map-preview__link">
          {mapArea}
        </a>
      </div>
    );
  }

  // Divs, not spans: chat bubbles force their own font size and colour onto spans.
  return (
    <div className="location-map-preview location-map-preview--card" style={{ '--lmp-size': sizeVar }}>
      <a href={url} target="_blank" rel="noopener noreferrer" className="location-map-preview__link" aria-label={sub ? `${title}: ${sub}` : title}>
        {mapArea}
        <div className="location-map-preview__text">
          <div className="location-map-preview__title">{title}</div>
          {sub ? <div className="location-map-preview__sub">{sub}</div> : null}
        </div>
      </a>
    </div>
  );
}
